import {
  getImageSource,
  Filter,
  FileExtension,
  ImageSourceOptions,
} from "./imagesService/getImageSource";
import {
  AspectRatio,
  getClosestAspectRatio,
  isValidAspectRatio,
} from "./domain/AspectRatio";
import { ImageSize, ImageSizeWithSource } from "./domain/ImageSize";
import { sizesByAspectRatio } from "./sizes";

export { ImageSize, AspectRatio };

const getImageSizes = (
  path: string,
  aspectRatio: AspectRatio | string,
  imageSourceOptions?: ImageSourceOptions
): ImageSizes => {
  let _aspectRatio: AspectRatio;

  if (isValidAspectRatio(aspectRatio)) {
    _aspectRatio = aspectRatio;
  } else {
    _aspectRatio = getClosestAspectRatio(aspectRatio);
  }

  const sizes: ImageSize[] = sizesByAspectRatio[_aspectRatio];

  const imageSizesArray = new ImageSizes();

  sizes.forEach(({ width, height }) =>
    imageSizesArray.push({
      width,
      height,
      src: getImageSource(path, {
        ...imageSourceOptions,
        width,
        height,
      }) as string,
    })
  );

  return imageSizesArray;
};

class ImageSizes extends Array<ImageSizeWithSource> {
  /**
   * Don't use this method directly, use ImagesService.getImageSizes() instead
   */
  constructor() {
    // Set the prototype explicitly, to successfully extend Array
    // See: https://github.com/Microsoft/TypeScript/wiki/FAQ#why-doesnt-extending-built-ins-like-error-array-and-map-work
    super();
    Object.setPrototypeOf(this, ImageSizes.prototype);
  }

  /**
   * Converts the ImageSizes object to a srcset string
   */
  getSrcset() {
    return this.reduce((srcset, { width, src }, i) => {
      return srcset + (i ? ", " : "") + `${src} ${width}w`;
    }, "");
  }

  /**
   * Get the closest size (from the standard set of sizes)
   * given the width or height passed as options
   */
  getClosestImageSize(options: { width?: number; height?: number }) {
    if (options.width !== undefined && options.height !== undefined) {
      throw new Error(
        "Either width or height should be passed as constraints, but not both"
      );
    }

    const constraint: "width" | "height" =
      options.width !== undefined ? "width" : "height";

    let i = 0;
    while (
      this[i][constraint] < (options[constraint] as number) &&
      this[i + 1]
    ) {
      i++;
    }

    return this[i];
  }
}

/** Create an instance of ImageService */
export const createImagesService = () => {
  return {
    getImageSizes,
    getImageSource,

    AspectRatio,
    Filter,
    FileExtension,
  };
};
